import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlarmClock,
  ArrowRightCircle,
  Award,
  Bell,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  Hourglass,
  Siren,
  XCircle,
} from 'lucide-react'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
  type AppNotification,
} from '@/lib/queries'
import {
  Button,
  EmptyState,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui'
import { cn, formatDateTime, formatRelative } from '@/lib/utils'
import { useI18n } from '@/i18n/I18nProvider'
import type { TranslationKey } from '@/i18n/dictionary'

/*
 * The icon name comes from the server so the meaning of a notification lives
 * in one place. This map is only the name-to-component lookup.
 */
const ICONS: Record<string, typeof Bell> = {
  'clipboard-list': ClipboardList,
  'check-circle-2': CheckCircle2,
  'x-circle': XCircle,
  'arrow-right-circle': ArrowRightCircle,
  award: Award,
  hourglass: Hourglass,
  siren: Siren,
  // Reminders. These were once missing, so the scheduled digests silently fell
  // back to a plain bell and every notification looked identical in the list.
  // An unknown name degrades quietly rather than failing, which is right at
  // runtime and exactly why the gap survived — so when adding a notification,
  // add its icon here too.
  'alarm-clock': AlarmClock,
  'clipboard-check': ClipboardCheck,
  bell: Bell,
}

export function NotificationBell() {
  const { t, tp } = useI18n()
  const [open, setOpen] = useState(false)
  const unread = useUnreadCount()
  const count = unread.data ?? 0

  const label =
    count > 0
      ? `${t('notifications.title')} — ${tp('notifications.unread', count)}`
      : t('notifications.title')

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              onClick={() => setOpen(true)}
              className="hover:bg-muted focus-visible:ring-ring/50 relative flex size-11 items-center justify-center rounded-xl transition-colors focus-visible:ring-2 focus-visible:outline-none"
              // The count belongs in the label, not just the badge — a screen
              // reader user gets nothing from a coloured dot.
              aria-label={label}
            />
          }
        >
          <Bell className="size-5" aria-hidden />

          {count > 0 && (
            <span
              className="bg-brand-600 absolute top-1.5 right-1.5 flex min-w-4.5 items-center justify-center rounded-full px-1 text-[10px] font-semibold text-white"
              aria-hidden
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </TooltipTrigger>

        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>

      <NotificationPanel open={open} onClose={() => setOpen(false)} unreadCount={count} />
    </>
  )
}

function NotificationPanel({
  open,
  onClose,
  unreadCount,
}: {
  open: boolean
  onClose: () => void
  unreadCount: number
}) {
  const { t, tp } = useI18n()
  const navigate = useNavigate()
  // Only fetch the list once the panel is actually opened.
  const query = useNotifications(false)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const notifications = query.data?.data ?? []

  function handleOpen(notification: AppNotification) {
    if (!notification.isRead) markRead.mutate(notification.id)

    if (notification.path) {
      onClose()
      navigate(notification.path)
    }
  }

  return (
    /*
     * A side panel rather than a centred dialog.
     *
     * Notifications are a list you skim and dismiss, not a task you complete —
     * a box in the middle of the screen covering the page you were reading is
     * the wrong shape for that. The sheet slides in beside the content, is
     * full-height so more than three fit without scrolling, and on a phone it
     * covers the screen the way a notification drawer already does.
     */
    <Sheet open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col gap-0 p-0 sm:max-w-md',
          // Glass, so the page you were reading stays faintly visible behind
          // the panel rather than being replaced by it.
          'supports-[backdrop-filter]:bg-popover/85 backdrop-blur-2xl backdrop-saturate-150',
          'border-black/5 dark:border-white/10',
        )}
        aria-label={t('notifications.title')}
      >
        <SheetHeader className="border-b px-4 py-3">
          <SheetTitle>{t('notifications.title')}</SheetTitle>
          <SheetDescription>
            {unreadCount > 0
              ? tp('notifications.unread', unreadCount)
              : t('notifications.allRead')}
          </SheetDescription>
        </SheetHeader>

        {/* min-h-0 so this actually scrolls instead of pushing the footer off. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {query.isLoading && <Spinner />}

          {query.data && notifications.length === 0 && (
            <EmptyState
              icon={<Bell className="size-8" />}
              title={t('notifications.empty')}
              description={t('notifications.emptyHint')}
            />
          )}

          {groupByDay(notifications).map(([day, items]) => (
            <section key={day}>
              {/* Sticky, so you always know which day you are looking at while
                  scrolling back through a busy week. */}
              <h3 className="bg-background muted sticky top-0 z-10 px-2 py-1.5 text-xs font-semibold tracking-wide uppercase">
                {t(day)}
              </h3>

              <ul className="space-y-1 pb-2">
                {items.map((notification) => {
                  const Icon = ICONS[notification.icon] ?? Bell

                  return (
                    <li key={notification.id}>
                      <button
                        onClick={() => handleOpen(notification)}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-xl p-3 text-left transition-colors',
                          'hover:bg-muted focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
                        )}
                      >
                        {/* The icon gets a chip so unread rows are legible at a
                            glance without tinting the whole row, which made the
                            text harder to read than the ones already seen. */}
                        <span
                          className={cn(
                            'flex size-9 shrink-0 items-center justify-center rounded-lg',
                            notification.isRead
                              ? 'bg-muted text-muted-foreground'
                              : 'bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300',
                          )}
                        >
                          <Icon className="size-4.5" aria-hidden />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span
                            className={cn(
                              'block text-sm',
                              notification.isRead ? 'font-medium' : 'font-semibold',
                            )}
                          >
                            {notification.title}
                          </span>
                          <span className="muted block text-xs">{notification.body}</span>
                          <span
                            className="muted mt-0.5 block text-xs"
                            title={formatDateTime(notification.at)}
                          >
                            {notification.context && `${notification.context} · `}
                            {formatRelative(notification.at)}
                          </span>
                        </span>

                        {/* A dot, not a "Novo" chip — it carries the same
                            meaning in a tenth of the width, which matters on a
                            row that already has three lines of text. */}
                        {!notification.isRead && (
                          <span
                            className="bg-brand-600 mt-1.5 size-2 shrink-0 rounded-full"
                            aria-label={t('notifications.unreadMarker')}
                          />
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>

        <SheetFooter className="mx-0 mb-0 border-t p-3">
          <Button
            variant="secondary"
            fullWidth
            onClick={() => markAll.mutate()}
            loading={markAll.isPending}
            disabled={unreadCount === 0}
          >
            {t('notifications.markAllRead')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Buckets the list into Danas / Juče / Ranije, keeping the server's order.
 *
 * Fifty rows of "prije 8 h" tell you nothing about where yesterday ended.
 */
function groupByDay(
  notifications: AppNotification[],
): [TranslationKey, AppNotification[]][] {
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)

  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfToday.getDate() - 1)

  const groups = new Map<TranslationKey, AppNotification[]>()

  for (const notification of notifications) {
    const at = notification.at ? new Date(notification.at) : null

    /*
     * Grouped by key rather than by translated heading.
     *
     * The key is also the Map's identity, so translating here would make
     * "Danas" and "Today" two different buckets — and a language switch with
     * the panel open would split one day's notifications across two headings.
     */
    const day: TranslationKey =
      at === null
        ? 'notifications.earlier'
        : at >= startOfToday
          ? 'notifications.today'
          : at >= startOfYesterday
            ? 'notifications.yesterday'
            : 'notifications.earlier'

    const bucket = groups.get(day)
    if (bucket) bucket.push(notification)
    else groups.set(day, [notification])
  }

  return [...groups.entries()]
}
